import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteMultipleReceipts } from '@/hooks/useInventoryReceipts';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import type { InventoryReceipt } from '@/types/entities';

interface DeleteReceiptDialogProps {
  restaurantId: string;
  receipts: InventoryReceipt[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DeleteReceiptDialog({
  restaurantId,
  receipts,
  open,
  onOpenChange,
}: DeleteReceiptDialogProps) {
  const { t, i18n } = useTranslation();
  const deleteMutation = useDeleteMultipleReceipts(restaurantId);
  const [isDeleting, setIsDeleting] = useState(false);

  const isGroupedDelete = receipts.length > 1;
  const totalCost = receipts.reduce((sum, r) => sum + r.totalCost, 0);

  // Check if receipts share same supplier/invoice (grouped invoice)
  const isInvoiceGroup =
    isGroupedDelete &&
    receipts.every((r) => r.supplier === receipts[0]?.supplier) &&
    receipts.every((r) => r.invoiceNumber === receipts[0]?.invoiceNumber);

  const handleDelete = async () => {
    setIsDeleting(true);
    const receiptIds = receipts.map((r) => r.id);

    deleteMutation.mutate(receiptIds, {
      onSettled: () => {
        setIsDeleting(false);
        onOpenChange(false);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isInvoiceGroup
              ? t('inventory.deleteReceipt.groupTitle')
              : t('inventory.deleteReceipt.title')}
          </DialogTitle>
          <DialogDescription>
            {t('inventory.deleteReceipt.confirmation')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Show grouped invoice details */}
          {isInvoiceGroup && (
            <div className="p-4 bg-muted rounded-md space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">
                  {t('inventory.receiptHistory.supplier')}:
                </span>
                <span className="text-sm">{receipts[0]?.supplier}</span>
              </div>
              {receipts[0]?.invoiceNumber && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">
                    {t('inventory.receiptHistory.invoiceNumber')}:
                  </span>
                  <span className="text-sm">{receipts[0]?.invoiceNumber}</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">{t('inventory.deleteReceipt.ingredients')}:</span>
                <Badge variant="secondary">{receipts.length}</Badge>
              </div>
            </div>
          )}

          {/* List all affected ingredients */}
          {isGroupedDelete && (
            <div className="max-h-[200px] overflow-y-auto space-y-2">
              <p className="text-sm font-medium">{t('inventory.deleteReceipt.groupWarning')}</p>
              <ul className="space-y-1">
                {receipts.map((receipt) => (
                  <li key={receipt.id} className="text-sm flex justify-between items-center p-2 bg-muted rounded">
                    <span>
                      {i18n.language === 'ar'
                        ? receipt.ingredient?.nameAr
                        : receipt.ingredient?.nameFr}
                    </span>
                    <span className="text-muted-foreground">
                      {receipt.quantityReceived} {receipt.unit}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Single receipt details */}
          {!isGroupedDelete && receipts[0] && (
            <div className="p-4 bg-muted rounded-md space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">
                  {t('inventory.receiptHistory.ingredient')}:
                </span>
                <span className="text-sm">
                  {i18n.language === 'ar'
                    ? receipts[0].ingredient?.nameAr
                    : receipts[0].ingredient?.nameFr}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">
                  {t('inventory.receiptHistory.quantity')}:
                </span>
                <span className="text-sm">
                  {receipts[0].quantityReceived} {receipts[0].unit}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">
                  {t('inventory.receiptHistory.totalCost')}:
                </span>
                <span className="text-sm">
                  {receipts[0].totalCost.toFixed(2)} {t('common.currency')}
                </span>
              </div>
            </div>
          )}

          {/* Total cost for grouped delete */}
          {isGroupedDelete && (
            <div className="flex justify-between items-center p-3 bg-primary/10 rounded-md">
              <span className="font-medium">{t('inventory.receiptHistory.totalCost')}:</span>
              <span className="font-bold">
                {totalCost.toFixed(2)} {t('common.currency')}
              </span>
            </div>
          )}

          {/* Warning */}
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <p className="text-sm">{t('inventory.deleteReceipt.inventoryWarning')}</p>
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isDeleting}>
            {t('common.cancel')}
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
            {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('common.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
